import { useMemo } from "react";
import {
  Box,
  Flex,
  Heading,
  Table,
  TableContainer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CATEGORICAL, COLORS } from "../../theme/dashboardTokens";
import { formatGriValue } from "./griFormat";
import PaginatedIndicatorGrid from "./PaginatedIndicatorGrid";
import { usePagedList } from "../../hooks/usePagedList";
import { Pagination } from "../Pagination";
import { ordenarSeries, rotuloDaSerie } from "../../pages/gri-indicators/series";

// GRI-05 (CA05) — comparação nomeada entre empresas escolhidas pelo MASTER.
// Sem anonimização de propósito (diferente do benchmark do GRI-04): é visão
// interna do admin, não algo exposto a uma empresa vendo a concorrência.

function companyColor(index) {
  return CATEGORICAL[index % CATEGORICAL.length];
}

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <Box bg={COLORS.surface} border="1px solid #E2E8F0" borderRadius="8px" p="10px" boxShadow="sm">
      <Text fontSize="13px" fontWeight={600} color={COLORS.ink} mb="4px">
        {label}
      </Text>
      {payload.map((p) => (
        <Text key={p.name} fontSize="12px" color={COLORS.inkMuted}>
          {p.name}: <b>{formatGriValue(p.value, unit)}</b>
        </Text>
      ))}
    </Box>
  );
}

export default function GriCompareDashboard({ entries, companies, selectedYears }) {
  const sortedYears = useMemo(() => [...selectedYears].sort((a, b) => a - b), [selectedYears]);

  // Séries presentes nas empresas comparadas, não uma lista fixa de 4 — uma
  // série nova ganha aba sozinha assim que tiver dado.
  const seriesDisponiveis = useMemo(
    () => ordenarSeries([...new Set((entries || []).map((e) => e.series))]),
    [entries]
  );

  const bySeries = useMemo(() => {
    const grouped = new Map();
    seriesDisponiveis.forEach((s) => grouped.set(s, new Map()));
    (entries || []).forEach((entry) => {
      if (!selectedYears.includes(entry.year)) return;
      const bucket = grouped.get(entry.series);
      if (!bucket) return;
      if (!bucket.has(entry.id_gri_indicator)) {
        bucket.set(entry.id_gri_indicator, { code: entry.code, name: entry.name, unit: entry.unit, byCompanyYear: {} });
      }
      const indicator = bucket.get(entry.id_gri_indicator);
      if (!indicator.byCompanyYear[entry.id_company]) indicator.byCompanyYear[entry.id_company] = {};
      const numeric = Number(entry.value);
      indicator.byCompanyYear[entry.id_company][entry.year] = Number.isFinite(numeric) ? numeric : entry.value;
    });
    return grouped;
  }, [entries, selectedYears, seriesDisponiveis]);

  // Lista achatada pra tabela: 1 linha por (indicador, ano) com dado real —
  // separado do JSX pra poder paginar (Pagination espera um array, não uma
  // árvore de <Tr> já montada).
  const tableRows = useMemo(() => {
    const rows = [];
    seriesDisponiveis.forEach((s) => {
      [...(bySeries.get(s)?.values() || [])].forEach((indicator) => {
        sortedYears
          .filter((year) => companies?.some((c) => indicator.byCompanyYear[c.id_company]?.[year] != null))
          .forEach((year) => rows.push({ key: `${s}-${indicator.code}-${year}`, indicator, year }));
      });
    });
    return rows;
  }, [bySeries, sortedYears, companies, seriesDisponiveis]);

  const tablePage = usePagedList(tableRows, 15);

  if (!companies || companies.length < 2) {
    return (
      <Flex bg={COLORS.surface} borderRadius="12px" p={["32px", "48px"]} direction="column" align="center" textAlign="center" gap="8px">
        <Heading size="sm" color={COLORS.ink}>
          Escolha ao menos 2 empresas
        </Heading>
        <Text color={COLORS.inkMuted} fontSize="14px" maxW="420px">
          Selecione as empresas que quer comparar no filtro acima.
        </Text>
      </Flex>
    );
  }

  return (
    <Box>
      <Box bg={COLORS.surface} borderRadius="12px" p={["20px", "28px"]} mb="24px">
        <Heading size="sm" color={COLORS.ink} mb="4px">
          Comparativo por indicador
        </Heading>
        <Text fontSize="12px" color={COLORS.inkMuted} mb="16px">
          {sortedYears.length > 1
            ? `Todos os anos selecionados (${sortedYears.join(", ")}).`
            : `Ano ${sortedYears[0] ?? "—"}.`}
        </Text>
        <TableContainer>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Indicador</Th>
                {sortedYears.length > 1 && <Th>Ano</Th>}
                {companies.map((c) => (
                  <Th key={c.id_company} isNumeric>
                    {c.companyName}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {tablePage.pageItems.map(({ key, indicator, year }) => (
                <Tr key={key}>
                  <Td>
                    <Text fontSize="13px" fontWeight={600} color={COLORS.ink}>
                      {indicator.code}
                    </Text>
                    <Text fontSize="11px" color={COLORS.inkMuted}>
                      {indicator.name}
                    </Text>
                  </Td>
                  {sortedYears.length > 1 && (
                    <Td fontSize="13px" color={COLORS.inkMuted} whiteSpace="nowrap">
                      {year}
                    </Td>
                  )}
                  {companies.map((c) => {
                    const value = indicator.byCompanyYear[c.id_company]?.[year];
                    return (
                      <Td key={c.id_company} fontSize="13px" fontWeight={600} isNumeric whiteSpace="nowrap">
                        {formatGriValue(value, indicator.unit)}
                      </Td>
                    );
                  })}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        {tableRows.length > 15 && (
          <Pagination
            currentPage={tablePage.page}
            onPageChange={tablePage.setPage}
            meta={tablePage.meta}
            viewing={tablePage.pageItems.length}
          />
        )}
      </Box>

      <Box bg={COLORS.surface} borderRadius="12px" p={["20px", "28px"]}>
        <Tabs variant="line" colorScheme="blue" isLazy>
          <TabList overflowX="auto" overflowY="hidden">
            {seriesDisponiveis.map((s) => (
              <Tab key={s} fontWeight={600} whiteSpace="nowrap">
                {rotuloDaSerie(s)} ({bySeries.get(s)?.size ?? 0})
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {seriesDisponiveis.map((s) => {
              const indicators = [...(bySeries.get(s)?.values() || [])];
              return (
                <TabPanel key={s} px={0} pt="20px">
                  <PaginatedIndicatorGrid
                    indicators={indicators}
                    renderCard={(indicator) => {
                      const rows = selectedYears.map((year) => {
                        const row = { ano: String(year) };
                        companies.forEach((c) => {
                          row[`empresa_${c.id_company}`] = indicator.byCompanyYear[c.id_company]?.[year] ?? null;
                        });
                        return row;
                      });
                      return (
                        <Box key={indicator.code} border="1px solid #EDF2F7" borderRadius="10px" p="16px">
                          <Text fontSize="13px" fontWeight={600} color={COLORS.ink}>
                            {indicator.code} — {indicator.name}
                          </Text>
                          <Text fontSize="11px" color={COLORS.inkMuted} mb="12px">
                            Unidade: {indicator.unit || "—"}
                          </Text>
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
                              <XAxis dataKey="ano" tick={{ fontSize: 11, fill: COLORS.inkMuted }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                              <YAxis tick={{ fontSize: 11, fill: COLORS.inkMuted }} tickLine={false} axisLine={false} />
                              <Tooltip
                                content={(props) => <ChartTooltip {...props} unit={indicator.unit} />}
                                cursor={{ fill: "rgba(0,115,187,0.06)" }}
                              />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              {companies.map((c, index) => (
                                <Bar
                                  key={c.id_company}
                                  dataKey={`empresa_${c.id_company}`}
                                  name={c.companyName}
                                  fill={companyColor(index)}
                                  radius={[4, 4, 0, 0]}
                                  maxBarSize={28}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      );
                    }}
                  />
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
